import { motion } from "framer-motion";
import { Mountain, Tent, Users, Camera, CalendarDays, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { SectionHeading } from "@/components/Section";
import { scrollToSection } from "@/scroll";

const IMG = {
  offroad:
    "https://images.unsplash.com/photo-1536383794906-773f6a5210be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  adventure:
    "https://images.unsplash.com/photo-1605019659411-f6dd1d229f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  community:
    "https://images.unsplash.com/photo-1672334440489-7945a743b823?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  media:
    "https://images.unsplash.com/photo-1485465109746-b76ae8d7eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxvZmZyb2FkJTIwNHg0JTIwZGVzZXJ0JTIwbXVkfGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
  events:
    "https://images.unsplash.com/photo-1636138105085-3e9381a8e438?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxvdmVybGFuZCUyMGNhbXBpbmclMjA0eDQlMjBleHBlZGl0aW9ufGVufDB8fHx8MTc4ODg5ODM3OHww&ixlib=rb-4.1.0&q=85",
};

const icons = { offroad: Mountain, adventure: Tent, community: Users, media: Camera, events: CalendarDays };

const targets = {
  offroad: "#eventi",
  adventure: "#eventi",
  community: "#community",
  media: "#media",
  events: "#eventi",
};

const layout = [
  "md:col-span-2 min-h-[440px]",
  "md:col-span-2 min-h-[440px]",
  "md:col-span-2 min-h-[440px]",
  "md:col-span-3 min-h-[380px]",
  "md:col-span-3 min-h-[380px]",
];

export default function Pillars() {
  const { t } = useLanguage();
  return (
    <section id="pilastri" className="py-28 sm:py-36 bg-surface/40 border-y border-line" data-testid="pillars-section">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <SectionHeading eyebrow={t.pillars.eyebrow} title={t.pillars.title} />
        <div className="grid md:grid-cols-6 gap-4">
          {t.pillars.items.map((p, i) => {
            const Icon = icons[p.key];
            return (
              <motion.div
                key={p.key}
                data-testid={`pillar-card-${p.key}`}
                initial={{ opacity: 0, y: 44 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.09, ease: [0.16, 1, 0.3, 1] }}
                className={`group relative overflow-hidden border border-line bg-ink ${layout[i]}`}
              >
                <img
                  src={IMG[p.key]}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover warm-grade opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-[opacity,transform] duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/10" />
                <div className="relative h-full flex flex-col justify-between p-6 sm:p-7">
                  <span className="self-start font-telemetry text-[10px] uppercase tracking-[0.25em] text-ochre border border-ochre/30 bg-ink/60 backdrop-blur-sm px-2.5 py-1">
                    {p.tag}
                  </span>
                  <div>
                    <Icon size={26} strokeWidth={1.5} className="text-bone mb-3 group-hover:text-blaze transition-colors duration-300" />
                    <h3 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-bone leading-none">
                      {p.title}
                    </h3>
                    <p className="mt-2.5 text-xs sm:text-sm leading-relaxed text-sand/85 max-w-sm">
                      {p.text}
                    </p>
                    <button
                      data-testid={`pillar-cta-${p.key}`}
                      onClick={() => scrollToSection(targets[p.key])}
                      className="mt-5 flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.25em] text-bone hover:text-blaze transition-colors duration-200"
                    >
                      {t.pillars.cta}
                      <ChevronRight size={13} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-blaze group-hover:w-full transition-[width] duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
